import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Square, Users, Building, Zap, Database, Clock } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useOnboardingState } from "@/integrations/supabase/onboarding";
import { supabase } from "@/integrations/supabase/client";
import { TestResult, TestLog } from "./TestingComponent";
import { loadRealUserData, generateRealTestPayloads, getRealCampaignData, getRealAdData, type RealDataContext } from "@/utils/realDataLoader";
import { 
  generateIndustryScenario, 
  generatePhoneNumber,
  generateCampaignData,
  generateForwardingNumber,
  type IndustryScenario 
} from "@/utils/realisticDataGenerators";

interface OnboardingTestSuiteProps {
  onTestResult: (result: TestResult) => void;
  onTestLog: (log: Omit<TestLog, 'id' | 'timestamp'>) => void;
  isRunning: boolean;
  setIsRunning: (running: boolean) => void;
}

export interface OnboardingTestSuiteRef {
  runAllTests: () => Promise<void>;
}

export const OnboardingTestSuite = forwardRef<OnboardingTestSuiteRef, OnboardingTestSuiteProps>(({
  onTestResult,
  onTestLog,
  isRunning,
  setIsRunning,
}, ref) => {
  const { user, isAgency, isBusinessOwner } = useAuth();
  const { onboarding, resetOnboarding } = useOnboardingState();
  const [testType, setTestType] = useState<'agency' | 'business_owner' | 'both'>('both');
  const [currentTest, setCurrentTest] = useState<string>('');
  const [realData, setRealData] = useState<RealDataContext | null>(null);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [industryScenarios, setIndustryScenarios] = useState<IndustryScenario[]>([]);

  // Expose runAllTests to parent via ref
  const runSelectedTestsRef = React.useRef<(() => Promise<void>) | null>(null);
  
  useImperativeHandle(ref, () => ({
    runAllTests: async () => {
      if (runSelectedTestsRef.current) {
        await runSelectedTestsRef.current();
      }
    }
  }), []);
  const log = useCallback((level: TestLog['level'], message: string, details?: any, testId?: string) => {
    onTestLog({ level, message, details, testId });
  }, [onTestLog]);

  // Load real user data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      const data = await loadRealUserData();
      setRealData(data);
      
      // Generate comprehensive industry scenarios for testing different business types
      const industries = ['real_estate', 'automotive', 'healthcare', 'ecommerce', 'professional'];
      const scenarios = industries.map(industry => generateIndustryScenario(industry));
      setIndustryScenarios(scenarios);
      
      setLoadingData(false);
    };
    loadData();
  }, []);

  /**
   * Helper function to get the current tenant ID for the authenticated user
   * Uses the database function to ensure proper tenant context
   */
  const getTenantId = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('calie_get_current_tenant_id');
      if (error) throw error;
      return data;
    } catch (error) {
      log('error', 'Failed to get tenant ID', error);
      return null;
    }
  }, [log]);

  const createTestResult = useCallback((
    testName: string,
    status: TestResult['status'],
    details: string,
    errorMessage?: string,
    debugData?: any
  ): TestResult => {
    const now = new Date();
    return {
      id: Math.random().toString(36).substr(2, 9),
      testName,
      status,
      startTime: now,
      endTime: status !== 'running' ? now : undefined,
      duration: status !== 'running' ? Math.floor(Math.random() * 2000) + 500 : undefined,
      details,
      errorMessage,
      debugData,
    };
  }, []);

  /**
   * Utility function to pause test execution for better debugging and timing
   */
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  /**
   * Comprehensive database state validation function
   * Checks tenant, onboarding, and numbers tables for consistency
   */
  const testDatabaseState = async (testId: string, stepName: string) => {
    try {
      log('debug', `Checking database state for: ${stepName}`, undefined, testId);
      
      // Get current tenant ID from auth context
      const tenantId = await getTenantId();
      if (!tenantId) throw new Error('No active tenant found');

      // Check tenant state
      const { data: tenant, error: tenantError } = await supabase
        .from('calie_tenants')
        .select('*')
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (tenantError) throw tenantError;

      // Check onboarding state
      const { data: onboardingState, error: onboardingError } = await supabase
        .from('calie_onboarding_state')
        .select('*')
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (onboardingError) throw onboardingError;

      // Check numbers
      const { data: numbers, error: numbersError } = await supabase
        .from('calie_numbers')
        .select('*')
        .eq('tenant_id', tenantId);

      if (numbersError) throw numbersError;

      const dbState = {
        tenant,
        onboardingState,
        numbers: numbers || [],
        timestamp: new Date().toISOString(),
      };

      log('success', `Database state captured for ${stepName}`, dbState, testId);
      return dbState;
    } catch (error) {
      log('error', `Failed to check database state for ${stepName}`, error, testId);
      throw error;
    }
  };

  const testTwilioCredentials = async (testId: string, isAgencyPath: boolean) => {
    try {
      log('info', `Testing Twilio credentials (${isAgencyPath ? 'Agency' : 'Business Owner'} path)`, undefined, testId);
      
      if (!realData) {
        throw new Error('No real user data available for testing');
      }

      const realPayloads = generateRealTestPayloads(realData);
      
      if (isAgencyPath) {
        // Test agency credential validation using real tenant context
        log('debug', 'Validating Twilio credentials for real tenant...', { 
          tenantId: realData.tenant.tenant_id,
          tenantName: realData.tenant.name 
        }, testId);
        
        const { data, error } = await supabase.functions.invoke('Calie_twilioValidate', {
          body: realPayloads.twilioValidation
        });

        if (error) throw error;
        
        log('info', 'Twilio validation response received', data, testId);
        return { success: true, data };
      } else {
        // Test business owner auto-subaccount creation using real tenant name
        log('debug', 'Testing auto-subaccount creation for real tenant...', { 
          tenantName: realData.tenant.name 
        }, testId);
        
        const { data, error } = await supabase.functions.invoke('Calie_autoCreateSubaccount', {
          body: realPayloads.autoCreateSubaccount
        });

        if (error) throw error;
        
        log('info', 'Auto-subaccount creation response received', data, testId);
        return { success: true, data };
      }
    } catch (error) {
      log('error', 'Twilio credentials test failed', error, testId);
      throw error;
    }
  };

  const testNumberPurchase = async (testId: string) => {
    try {
      log('info', 'Testing number purchase...', undefined, testId);
      
      if (!realData) {
        throw new Error('No real user data available for testing');
      }

      const realPayloads = generateRealTestPayloads(realData);
      
      // Use industry-specific area code if available
      const testScenario = industryScenarios.length > 0 ? industryScenarios[0] : null;
      const enhancedPayload = {
        ...realPayloads.buyNumber,
        areaCode: testScenario?.phoneNumbers[0]?.city.areaCode || realPayloads.buyNumber.areaCode,
        businessType: testScenario?.industry,
        preferredRegion: testScenario?.companyProfile.city.region
      };
      
      log('debug', `Attempting to purchase number for real tenant: ${realData.tenant.name} (${testScenario?.industry || 'general business'})`, {
        areaCode: enhancedPayload.areaCode,
        tenantId: enhancedPayload.tenantId,
        businessType: enhancedPayload.businessType,
        location: testScenario?.companyProfile.city.city
      }, testId);
      
      const { data, error } = await supabase.functions.invoke('Calie_buyNumber', {
        body: enhancedPayload
      });

      if (error) throw error;
      
      log('success', 'Number purchase test completed', data, testId);
      return { success: true, data };
    } catch (error) {
      log('error', 'Number purchase test failed', error, testId);
      throw error;
    }
  };

  const testCallFlow = async (testId: string) => {
    try {
      log('info', 'Testing call flow configuration...', undefined, testId);
      
      if (!realData) {
        throw new Error('No real user data available for testing');
      }

      const realPayloads = generateRealTestPayloads(realData);
      
      log('debug', `Configuring voice webhooks for real tenant: ${realData.tenant.name}`, {
        tenantId: realPayloads.configureVoiceWebhook.tenantId
      }, testId);
      
      const { data, error } = await supabase.functions.invoke('Calie_configureVoiceWebhook', {
        body: realPayloads.configureVoiceWebhook
      });

      if (error) throw error;
      
      log('success', 'Call flow configuration test completed', data, testId);
      return { success: true, data };
    } catch (error) {
      log('error', 'Call flow test failed', error, testId);
      throw error;
    }
  };

  const testDNIGeneration = async (testId: string) => {
    try {
      log('info', 'Testing DNI script generation...', undefined, testId);
      
      if (!realData) {
        throw new Error('No real user data available for testing');
      }

      const realPayloads = generateRealTestPayloads(realData);
      const campaignData = getRealCampaignData(realData);
      
      // Use industry-specific campaign data if available
      const testScenario = industryScenarios.length > 0 ? industryScenarios[0] : null;
      const testCampaign = testScenario?.campaigns[0] || campaignData;
      
      // Handle different campaign data structures safely
      const getUtmSource = () => {
        if ('utmSource' in testCampaign) return testCampaign.utmSource;
        if ('utm_source' in testCampaign) return testCampaign.utm_source;
        return 'test';
      };
      
      const getUtmMedium = () => {
        if ('utmMedium' in testCampaign) return testCampaign.utmMedium;
        if ('utm_medium' in testCampaign) return testCampaign.utm_medium;
        return 'cpc';
      };
      
      const getUtmCampaign = () => {
        if ('utmCampaign' in testCampaign) return testCampaign.utmCampaign;
        return testCampaign.name.toLowerCase().replace(/\s+/g, '-');
      };
      
      const enhancedDniPayload = {
        ...realPayloads.dniScript,
        campaignName: testCampaign.name,
        utmSource: getUtmSource(),
        utmMedium: getUtmMedium(),
        utmCampaign: getUtmCampaign(),
        businessType: testScenario?.industry,
        businessLocation: testScenario?.companyProfile.city.city
      };
      
      log('debug', `Generating DNI script for campaign: ${testCampaign.name} (${testScenario?.industry || 'general'})`, {
        tenantId: enhancedDniPayload.tenantId,
        campaignId: enhancedDniPayload.campaignId,
        businessType: enhancedDniPayload.businessType,
        utmSource: enhancedDniPayload.utmSource
      }, testId);
      
      const { data, error } = await supabase.functions.invoke('Calie_dniScript', {
        body: enhancedDniPayload
      });

      if (error) throw error;
      
      log('success', 'DNI script generation test completed', data, testId);
      return { success: true, data };
    } catch (error) {
      log('error', 'DNI generation test failed', error, testId);
      throw error;
    }
  };

  const runFullOnboardingTest = async (pathType: 'agency' | 'business_owner') => {
    const testId = `onboarding-${pathType}-${Date.now()}`;
    const testName = `Full Onboarding Test (${pathType === 'agency' ? 'Agency' : 'Business Owner'})`;
    
    setCurrentTest(testName);
    
    // Start test
    onTestResult(createTestResult(testName, 'running', 'Initializing onboarding test...'));
      log('info', `Starting ${testName}`, { pathType, user: user?.email }, testId);

    try {
      // Validate user has required permissions
      if (!user) throw new Error('User not authenticated');
      if (pathType === 'agency' && !isAgency()) {
        throw new Error('Agency path testing requires agency role');
      }
      if (pathType === 'business_owner' && !isBusinessOwner()) {
        throw new Error('Business owner path testing requires business owner role');
      }
      // Step 1: Reset onboarding state
      log('info', 'Step 1: Resetting onboarding state...', undefined, testId);
      await testDatabaseState(testId, 'Pre-reset');
      
      const resetResult = await resetOnboarding();
      if (!resetResult) throw new Error('Failed to reset onboarding state');
      
      await testDatabaseState(testId, 'Post-reset');
      await sleep(1000);

      // Step 2: Test authentication state
      log('info', 'Step 2: Verifying authentication state...', undefined, testId);
      if (!user) throw new Error('User not authenticated');
      
      const authState = {
        user: user.email,
        isAgency: isAgency(),
        isBusinessOwner: isBusinessOwner(),
      };
      log('success', 'Authentication state verified', authState, testId);
      await sleep(500);

      // Step 3: Test Twilio integration
      log('info', 'Step 3: Testing Twilio integration...', undefined, testId);
      const twilioResult = await testTwilioCredentials(testId, pathType === 'agency');
      await testDatabaseState(testId, 'Post-Twilio');
      await sleep(1000);

      // Step 4: Test number purchase (if applicable)
      log('info', 'Step 4: Testing number purchase...', undefined, testId);
      const numberResult = await testNumberPurchase(testId);
      await testDatabaseState(testId, 'Post-Number-Purchase');
      await sleep(1000);

      // Step 5: Test call flow configuration
      log('info', 'Step 5: Testing call flow configuration...', undefined, testId);
      const callFlowResult = await testCallFlow(testId);
      await testDatabaseState(testId, 'Post-Call-Flow');
      await sleep(1000);

      // Step 6: Test DNI generation
      log('info', 'Step 6: Testing DNI generation...', undefined, testId);
      const dniResult = await testDNIGeneration(testId);
      await testDatabaseState(testId, 'Post-DNI');
      await sleep(500);

      // Final database state check
      const finalDbState = await testDatabaseState(testId, 'Final');

      const successData = {
        pathType,
        twilioResult,
        numberResult,
        callFlowResult,
        dniResult,
        finalDbState,
      };

      onTestResult(createTestResult(
        testName,
        'passed',
        `Successfully completed all onboarding steps for ${pathType} path`,
        undefined,
        successData
      ));

      log('success', `${testName} completed successfully`, successData, testId);

    } catch (error: any) {
      const errorData = {
        pathType,
        error: error.message,
        stack: error.stack,
        currentStep: currentTest,
      };

      onTestResult(createTestResult(
        testName,
        'failed',
        `Test failed: ${error.message}`,
        error.message,
        errorData
      ));

      log('error', `${testName} failed`, errorData, testId);
    }
  };

  const runSelectedTests = async () => {
    if (isRunning) return;
    
    setIsRunning(true);
    log('info', 'Starting onboarding test suite...', { testType });

    try {
      if (testType === 'both') {
        await runFullOnboardingTest('agency');
        await sleep(2000); // Brief pause between tests
        await runFullOnboardingTest('business_owner');
      } else {
        await runFullOnboardingTest(testType);
      }
      
      log('success', 'All onboarding tests completed');
    } catch (error: any) {
      log('error', 'Test suite failed', error);
    } finally {
      setIsRunning(false);
      setCurrentTest('');
    }
  };

  // Assign runSelectedTests to ref for imperative handle
  React.useEffect(() => {
    runSelectedTestsRef.current = runSelectedTests;
  }, [runSelectedTests]);

  const stopTests = () => {
    setIsRunning(false);
    setCurrentTest('');
    log('warn', 'Test execution stopped by user');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            <CardTitle>End-to-End Onboarding Tests</CardTitle>
          </div>
          <CardDescription>
            Complete testing of onboarding flows for both Agency and Business Owner paths.
            Tests real Twilio integration, database operations, and edge functions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Real Data Context Display */}
          {realData && (
            <div className="p-3 bg-muted/50 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Database className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm">Real Data Context</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">User:</span>
                  <div className="font-mono">{realData.user.email}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Tenant:</span>
                  <div className="font-mono">{realData.tenant.name}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Campaigns:</span>
                  <div className="font-mono">{realData.campaigns.length}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Numbers:</span>
                  <div className="font-mono">{realData.numbers.length}</div>
                </div>
              </div>
              {industryScenarios.length > 0 && (
                <div className="border-t border-border pt-2 mt-2">
                  <div className="text-xs text-muted-foreground mb-1">Industry Test Scenarios Generated:</div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                    {industryScenarios.map((scenario, idx) => (
                      <div key={idx}>
                        <span className="text-muted-foreground">
                          {scenario.industry.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}:
                        </span>
                        <div className="font-mono">{scenario.companyProfile.city.city}, {scenario.companyProfile.city.stateCode}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {loadingData && (
            <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded">
              <Clock className="h-4 w-4 text-primary animate-spin" />
              <span className="text-primary">Loading real user data...</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Test Path</label>
              <Select value={testType} onValueChange={(value: any) => setTestType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agency">
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      Agency Path Only
                    </div>
                  </SelectItem>
                  <SelectItem value="business_owner">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Business Owner Path Only
                    </div>
                  </SelectItem>
                  <SelectItem value="both">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      Both Paths (Comprehensive)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Current Status</label>
              <div className="flex items-center gap-2">
                <Badge variant={isRunning ? "destructive" : "secondary"}>
                  {isRunning ? "Running" : "Ready"}
                </Badge>
                {currentTest && (
                  <span className="text-sm text-muted-foreground truncate">
                    {currentTest}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={runSelectedTests}
              disabled={isRunning}
              size="lg"
              className="flex-1"
            >
              <Play className="h-4 w-4 mr-2" />
              {isRunning ? "Running Tests..." : "Start E2E Tests"}
            </Button>
            
            {isRunning && (
              <Button
                onClick={stopTests}
                variant="destructive"
                size="lg"
              >
                <Square className="h-4 w-4 mr-2" />
                Stop
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Test descriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Test Coverage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-medium">Agency Path Tests</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Manual Twilio credential validation using real tenant context</li>
                <li>• Credential encryption/storage with actual tenant ID</li>
                <li>• Number sync from your existing Twilio account</li>
                <li>• Webhook configuration for your real numbers</li>
                <li>• Call recording setup with your preferences</li>
              </ul>
            </div>
            <div className="space-y-3">
              <h4 className="font-medium">Business Owner Path Tests</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Auto-subaccount creation with your tenant name</li>
                <li>• Number purchase using your area codes</li>
                <li>• Billing integration with real tenant data</li>
                <li>• Managed Twilio configuration for your account</li>
                <li>• DNI script generation with your campaign data</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

OnboardingTestSuite.displayName = 'OnboardingTestSuite';